/**
 * Curl switcher — carries the shopper's Length and Colour across to the curl they
 * pick, instead of dropping them on the target product's default variant.
 *
 * The hand-over goes through ?option_values=<ids>, which Shopify resolves to a
 * variant server-side with no cap. It must not go through a variant list: both
 * /products/{handle}.js and Liquid's product.variants stop at 250 variants, and
 * the tape products carry 630, so a list lookup never saw the 20"+ lengths and
 * dropped those shoppers on the default variant. The ids come from the
 * bw-option-probe section rendered on the target product's own URL.
 *
 * Progressive enhancement only. Every entry is a real <a href> to the product, so
 * with this script blocked, slow or erroring the switcher still works; the shopper
 * just lands on the default variant. Any failure below falls through to that href.
 */
class CurlSwitcher extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const link = event.target.closest('a[data-curl-handle]');
    if (!link) return;
    // Let modified clicks (new tab, download, middle click) behave normally.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const chosen = this.#currentOptions();
    if (!Object.keys(chosen).length) return;

    event.preventDefault();
    this.#go(link, chosen);
  };

  /**
   * The shopper's current selection, read from the DOM rather than from Liquid:
   * the variant picker re-renders on every change but this switcher sits outside
   * that subtree, so anything baked in at page load would go stale.
   * @returns {Record<string, string>} option name (lowercased) -> chosen value
   */
  #currentOptions() {
    const picker = document.querySelector('variant-picker');
    if (!picker) return {};

    /** @type {Record<string, string>} */
    const chosen = {};

    for (const select of picker.querySelectorAll('select[name^="options["]')) {
      const name = select.name.match(/options\[(.+)\]/)?.[1];
      if (name && select.value) chosen[name.trim().toLowerCase()] = select.value;
    }

    for (const radio of picker.querySelectorAll('fieldset input[type="radio"]:checked')) {
      const name = radio.closest('fieldset')?.querySelector('legend')?.textContent?.trim();
      if (name && radio.value) chosen[name.toLowerCase()] = radio.value;
    }

    return chosen;
  }

  /**
   * @param {HTMLAnchorElement} link
   * @param {Record<string, string>} chosen
   */
  async #go(link, chosen) {
    const fallback = link.href;
    try {
      const res = await fetch(`/products/${link.dataset.curlHandle}?section_id=bw-option-probe`, {
        headers: { Accept: 'text/html' },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const probe = doc.querySelector('[data-bw-option-probe]');
      if (!probe) throw new Error('no option probe');

      /** @type {{ options: { name: string, values: { value: string, id: number }[] }[] }} */
      const { options } = JSON.parse(probe.textContent || '{}');

      // An option the shopper has not set, or one this product does not share, is
      // simply left out; Shopify fills the gap from the first available variant.
      const ids = [];
      for (const option of options ?? []) {
        const want = chosen[option.name.trim().toLowerCase()];
        if (want === undefined) continue;
        const hit = option.values.find((v) => v.value === want);
        if (hit) ids.push(hit.id);
      }

      const url = new URL(fallback, window.location.href);
      if (ids.length) url.searchParams.set('option_values', ids.join(','));
      window.location.href = url.href;
    } catch {
      window.location.href = fallback;
    }
  }
}

if (!customElements.get('curl-switcher')) {
  customElements.define('curl-switcher', CurlSwitcher);
}
